/**
 * Preserves the URL a user was trying to reach when ProtectedRoute kicked
 * them out to /login. After Cognito hosted-UI sign-in, OAuthCallback reads
 * this back and navigates there instead of the default /scans landing.
 *
 * Storage: sessionStorage (not localStorage) so:
 *   - It survives the full-page redirect to Cognito and back (same tab).
 *   - It does NOT persist across closed tabs (a stale returnTo from
 *     last week's aborted sign-in shouldn't override today's intended
 *     landing).
 *
 * Sanitisation: we ONLY store same-origin relative paths starting with `/`.
 * Anything else (full URLs, protocol-relative, `javascript:` URIs) is
 * rejected to prevent open-redirect via Slack-link forging.
 */

const STORAGE_KEY = 'oauth.returnTo';

/**
 * Build a returnTo string from the current location. Combines pathname +
 * search + hash so query strings (`?status=approved`) and hash fragments
 * (#section) survive the round-trip. Returns null when the current
 * location is the login page or callback (no point looping back).
 */
export function buildReturnToFromLocation(location: Location): string | null {
  const path = `${location.pathname}${location.search}${location.hash}`;
  if (location.pathname === '/login' || location.pathname.startsWith('/oauth/')) {
    return null;
  }
  if (location.pathname === '/' || location.pathname === '') return null;
  return path;
}

export function storeReturnTo(returnTo: string | null): void {
  if (!returnTo) return;
  if (!isSafeReturnTo(returnTo)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, returnTo);
  } catch {
    // sessionStorage can throw in private-browsing modes — best-effort,
    // worst case is user lands on /scans which is the current behaviour.
  }
}

export function consumeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (!value) return null;
    return isSafeReturnTo(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Allow only same-origin relative paths. Rejects:
 *   - URLs starting with `//` (protocol-relative → could be cross-origin)
 *   - URLs starting with a non-/ char (e.g. `javascript:`, `data:`)
 *   - Whitespace-padded inputs (could mask the above checks)
 */
function isSafeReturnTo(value: string): boolean {
  if (value !== value.trim()) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  return true;
}
